import type { AgentRole } from '@/types'

/**
 * Product seats for the agent-first shell. Role slugs match the API
 * (`scout`, `analyst`, …). Realtor contacts stay on `/agents`.
 */
export interface AgentSeat {
  role: AgentRole
  label: string
  description: string
  adminOnly?: boolean
}

export const PRODUCT_SEATS: AgentSeat[] = [
  { role: 'scout', label: 'Scout', description: 'Leads and markets' },
  { role: 'analyst', label: 'Analyst', description: 'Deal analysis' },
  { role: 'marketer', label: 'Marketer', description: 'Campaigns and outreach' },
  { role: 'closer', label: 'Closer', description: 'Pipeline and deals' },
  { role: 'dispo', label: 'Dispo', description: 'Buyer matching' },
  { role: 'operator', label: 'Operator', description: 'Business metrics' },
]

export const ADMIN_SEATS: AgentSeat[] = [
  { role: 'sysadmin', label: 'Sysadmin', description: 'System administration', adminOnly: true },
  { role: 'billing-ops', label: 'Billing', description: 'Billing operations', adminOnly: true },
  { role: 'triage', label: 'Triage', description: 'Request routing', adminOnly: true },
]

export const ALL_SEATS: AgentSeat[] = [...PRODUCT_SEATS, ...ADMIN_SEATS]

const SEAT_ROLE_SET = new Set<string>(ALL_SEATS.map((seat) => seat.role))

export const SEAT_DOT: Record<string, string> = {
  scout: 'bg-blue-400',
  analyst: 'bg-violet-400',
  marketer: 'bg-emerald-400',
  closer: 'bg-amber-400',
  dispo: 'bg-cyan-400',
  operator: 'bg-zinc-300',
  sysadmin: 'bg-rose-400',
  'billing-ops': 'bg-lime-400',
  triage: 'bg-fuchsia-400',
}

export const LAST_SEAT_KEY = 'forge-last-agent-seat'

export function isSeatRole(role: string | null | undefined): role is AgentRole {
  return !!role && SEAT_ROLE_SET.has(role)
}

export function seatByRole(role: string | null | undefined): AgentSeat | undefined {
  return ALL_SEATS.find((seat) => seat.role === role)
}

export function seatLabel(role: string | null | undefined): string {
  return seatByRole(role)?.label ?? (role ? role : 'Agent')
}

export function visibleSeats(isAdmin: boolean): AgentSeat[] {
  return isAdmin ? ALL_SEATS : PRODUCT_SEATS
}

/** `/agents/:role` and `/ops` fill the content area (thread scroll, not page scroll). */
export function isAgentShellPath(pathname: string): boolean {
  if (pathname === '/ops') return true
  const match = pathname.match(/^\/agents\/([^/]+)$/)
  if (!match) return false
  return isSeatRole(decodeURIComponent(match[1]))
}

export function seatPath(role: string | null | undefined, conversationId?: string | null): string {
  const resolved = isSeatRole(role) ? role : 'scout'
  if (!conversationId) return `/agents/${resolved}`
  return `/agents/${resolved}?c=${encodeURIComponent(conversationId)}`
}

export function rememberSeat(role: string) {
  if (!isSeatRole(role)) return
  try {
    localStorage.setItem(LAST_SEAT_KEY, role)
  } catch {
    /* private mode */
  }
}

export function lastSeat(): AgentRole {
  try {
    const stored = localStorage.getItem(LAST_SEAT_KEY)
    if (isSeatRole(stored)) return stored
  } catch {
    /* ignore */
  }
  return 'scout'
}
